import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiShield, FiTarget, FiCheckCircle, FiEye, FiGitBranch, FiTrendingUp, FiSettings, FiLayers, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import ScrollHeader from '../components/ScrollHeader';
import MotionDiv from '../components/MotionDiv';

const ThreatModeling = () => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [currentResultIndex, setCurrentResultIndex] = useState(0);

  const currentStats = [
    { label: "Total Scans", value: "10", description: "threat modeling scans completed", icon: <FiTarget /> },
    { label: "Repositories Analyzed", value: "6", description: "repositories under threat analysis", icon: <FiGitBranch /> },
    { label: "Recommendations Generated", value: "155", description: "security recommendations", icon: <FiCheckCircle /> },
    { label: "Last Scan", value: "4", description: "days ago", icon: <FiTrendingUp /> }
  ];

  const stepByStepGuide = [
    {
      step: 1,
      title: "Assets Configuration",
      description: "Configure your asset details including risk level, sensitivity, business impact, and compliance frameworks.",
      image: "/screenshots/threat_modelling/step1.png",
      alt: "Step 1: Assets Configuration",
      infoType: "blue",
      infoTitle: "Key Configuration Options:",
      infoItems: [
        "Risk Level: Select from Critical, High, Medium, or Low",
        "Sensitivity Level: Define data sensitivity classification",
        "Business Impact: Assess potential business impact",
        "Data Classification: Specify data classification requirements",
        "Compliance Frameworks: HIPAA, SOX, HITRUST CSF, PCI DSS, FedRAMP, GDPR",
        "Security Standards: NIST Cybersecurity Framework, CIS Controls, COBIT, ISO 27001, NIST 800-53, OWASP ASVS"
      ]
    },
    {
      step: 2,
      title: "Components Selection",
      description: "Select the system components that will be included in your threat model analysis.",
      image: "/screenshots/threat_modelling/step2.png",
      alt: "Step 2: Components Selection",
      infoType: "green",
      infoTitle: "Available Component Categories:",
      infoItems: [
        "Backend Components: Application Server, Microservices, Monolithic Server, Batch Processing Server, Backend-as-a-Service (BaaS)",
        "Web and Frontend Components",
        "Mobile Application Components",
        "API and Integration Components",
        "Database and Storage Components",
        "Authentication and Authorization",
        "Cloud and Infrastructure Components"
      ]
    },
    {
      step: 3,
      title: "Diagrams Upload",
      description: "Upload system diagrams to provide visual context for your threat model analysis.",
      image: "/screenshots/threat_modelling/step3.png",
      alt: "Step 3: Diagrams Upload",
      infoType: "purple",
      infoTitle: "Supported Diagram Types:",
      infoItems: [
        "Data Flow Diagram: Shows how data flows through your system",
        "Architecture Diagram: Displays system architectural components and relationships",
        "Infrastructure Diagram: Illustrates deployment and infrastructure setup",
        "Supports image files only (PNG, JPG, SVG)",
        "Drag and drop interface for easy upload"
      ]
    },
    {
      step: 4,
      title: "Authentication & Authorization",
      description: "Configure authentication and authorization methods used in your system.",
      image: "/screenshots/threat_modelling/step4.png",
      alt: "Step 4: Authentication & Authorization",
      infoType: "orange",
      infoTitle: "Authentication & Authorization Options:",
      infoItems: [
        "Authentication Methods: OAuth 2.0, JSON Web Tokens (JWT), Single Sign-On (SSO), Multi-Factor Authentication (MFA), Basic Authentication, SAML, OpenID Connect, Biometric Authentication",
        "Authorization Methods: Role-Based Access Control (RBAC), Discretionary Access Control (DAC), Privileged Access Management (PAM), Policy-Based Access Control, Mandatory Access Control (MAC), Attribute-Based Access Control (ABAC), Access Control Lists (ACL), Contextual Access Control"
      ]
    },
    {
      step: 5,
      title: "Summary Review",
      description: "Review all your configuration settings before generating the threat analysis.",
      image: "/screenshots/threat_modelling/step5.png",
      alt: "Step 5: Summary Review",
      infoType: "indigo",
      infoTitle: "Summary Includes:",
      infoItems: [
        "Project Information: Name, description, repository URL, and tags",
        "Asset Classification: Risk level, sensitivity, business impact, and data classification",
        "Compliance Frameworks: Selected compliance requirements",
        "Security Standards: Applicable security standards",
        "Selected Components: All chosen system components",
        "Identified Threats: Preview of threat categories",
        "System Diagrams: Uploaded architectural diagrams",
        "Authentication & Authorization: Selected security methods"
      ]
    },
    {
      step: 6,
      title: "Generate Threat Analysis",
      description: "Click \"Generate Threat Analysis\" to start the comprehensive threat modeling process.",
      image: "/screenshots/threat_modelling/step6.png",
      alt: "Step 6: Generate Analysis",
      infoType: "green",
      infoTitle: "Analysis Generation Process:",
      infoItems: [
        "System analyzes all provided information",
        "Generates comprehensive threat model",
        "Creates detailed security recommendations",
        "Produces downloadable reports",
        "Progress tracking with completion percentage"
      ]
    }
  ];

  const analysisResults = [
    {
      title: "Analysis Progress",
      description: "The system shows real-time progress with completion percentage and provides access to different analysis tabs: STRIDE, ATTACK TREES, DREAD, TEST CASES, and MITIGATION.",
      image: "/screenshots/threat_modelling/result_0.png",
      alt: "Analysis Progress",
      infoType: "blue",
      infoTitle: "Progress Features:",
      infoItems: [
        "Real-time progress tracking with completion percentage",
        "Access to different analysis tabs",
        "STRIDE, ATTACK TREES, DREAD, TEST CASES, and MITIGATION tabs",
        "Interactive navigation between result sections"
      ]
    },
    {
      title: "STRIDE Analysis Results",
      description: "Comprehensive STRIDE analysis findings with detailed threat categorization and improvement suggestions.",
      image: "/screenshots/threat_modelling/result_1.png",
      alt: "STRIDE Analysis Results",
      infoType: "red",
      infoTitle: "STRIDE Analysis Findings:",
      infoItems: [
        "Spoofing: Fake AWS account creation and phishing attacks identified",
        "Tampering: GitHub webhook spoofing vulnerabilities",
        "Repudiation: Issues with audit trails and logging",
        "Information Disclosure: Sensitive data exposure risks",
        "Denial of Service: Service availability threats",
        "Elevation of Privilege: Unauthorized access escalation risks",
        "Detailed improvement suggestions provided for each category"
      ]
    },
    {
      title: "Attack Trees Visualization",
      description: "Interactive attack tree visualization showing hierarchical attack paths and scenarios.",
      image: "/screenshots/threat_modelling/result_2.png",
      alt: "Attack Trees",
      infoType: "yellow",
      infoTitle: "Attack Tree Features:",
      infoItems: [
        "Visual representation of attack paths and scenarios",
        "Interactive zoom and navigation controls",
        "Hierarchical display of attack vectors",
        "Full-screen viewing capability",
        "Detailed attack progression mapping"
      ]
    },
    {
      title: "DREAD Analysis Results",
      description: "Comprehensive DREAD scoring with risk assessment for each threat category.",
      image: "/screenshots/threat_modelling/result_3.png",
      alt: "DREAD Analysis",
      infoType: "purple",
      infoTitle: "DREAD Scoring Results:",
      infoItems: [
        "Spoofing: Total Score 100 (Critical)",
        "Tampering: Total Score 91 (High)",
        "Repudiation: Total Score 53 (Medium)",
        "Information Disclosure: Total Score 100 (Critical)",
        "Denial of Service: Total Score 99 (Critical)",
        "Elevation of Privilege: Total Score 102 (Critical)",
        "Expandable sections for detailed threat analysis"
      ]
    },
    {
      title: "Generated Test Cases (17)",
      description: "Comprehensive test cases generated based on identified threats and vulnerabilities.",
      image: "/screenshots/threat_modelling/result_4.png",
      alt: "Test Cases",
      infoType: "green",
      infoTitle: "Sample Test Cases Include:",
      infoItems: [
        "Test unauthorized AWS account creation for resource deployment",
        "Test phishing attack using fake WebGoat registration page",
        "Test spoofing of GitHub webhooks",
        "Test tampering of S3 bucket contents",
        "Test man-in-the-middle attack on AWS CloudFormation templates",
        "Test compromised pipeline configuration",
        "Test lack of logging in AWS CodePipeline",
        "Test accidental upload of sensitive data to public S3 buckets",
        "Each test case includes detailed scenario descriptions"
      ]
    },
    {
      title: "Mitigation Analysis",
      description: "Detailed mitigation strategies and recommendations for addressing identified threats.",
      image: "/screenshots/threat_modelling/result_5.png",
      alt: "Mitigation Analysis",
      infoType: "indigo",
      infoTitle: "Mitigation Strategies:",
      infoItems: [
        "Spoofing Mitigations: Account aliasing, tagging, AWS Organizations/Control Tower",
        "Phishing Protection: SPF, DKIM, DMARC implementation, user education",
        "Webhook Security: Secret tokens, IP whitelisting",
        "Tampering Prevention: Input validation, access controls",
        "Information Disclosure: Data encryption, access restrictions",
        "Denial of Service: Rate limiting, monitoring, redundancy",
        "Privilege Escalation: Least privilege principles, regular audits"
      ]
    }
  ];

  const coreFeatures = [
    {
      title: "Automated Threat Identification",
      icon: <FiTarget />,
      features: [
        "Attack Vector Analysis: Systematic identification of potential attack vectors",
        "Vulnerability Assessment: Comprehensive vulnerability identification",
        "Risk Prioritization: Automated risk scoring and prioritization",
        "Threat Classification: Categorization of threats by type and severity"
      ]
    },
    {
      title: "Security Architecture Analysis",
      icon: <FiLayers />,
      features: [
        "System Component Mapping: Identification of system components and their interactions",
        "Data Flow Analysis: Mapping of data flows and potential exposure points",
        "Trust Boundary Identification: Definition of security boundaries and trust zones",
        "Asset Inventory: Comprehensive cataloging of security-relevant assets"
      ]
    },
    {
      title: "Risk Assessment",
      icon: <FiShield />,
      features: [
        "Impact Analysis: Assessment of potential impact from identified threats",
        "Likelihood Evaluation: Probability assessment for threat realization",
        "Risk Scoring: Quantitative risk assessment using industry standards",
        "Mitigation Prioritization: Ranking of mitigation efforts by risk reduction"
      ]
    }
  ];

  const threatMethodologies = [
    {
      title: "STRIDE Framework",
      description: "Microsoft's threat modeling methodology",
      components: [
        "Spoofing: Identity spoofing threat analysis",
        "Tampering: Data and system tampering assessment",
        "Repudiation: Non-repudiation threat evaluation",
        "Information Disclosure: Data exposure risk analysis",
        "Denial of Service: Availability threat assessment",
        "Elevation of Privilege: Privilege escalation risk evaluation"
      ]
    },
    {
      title: "PASTA (Process for Attack Simulation and Threat Analysis)",
      description: "Risk-centric threat modeling methodology",
      components: [
        "Stage 1: Define business objectives and security requirements",
        "Stage 2: Define technical scope and architecture",
        "Stage 3: Application decomposition and analysis",
        "Stage 4: Threat analysis using multiple methodologies",
        "Stage 5: Vulnerability and weakness analysis",
        "Stage 6: Attack modeling and simulation",
        "Stage 7: Risk analysis and impact assessment"
      ]
    },
    {
      title: "DREAD Assessment",
      description: "Risk assessment methodology for threat prioritization",
      components: [
        "Damage: Potential damage from threat realization",
        "Reproducibility: Ease of reproducing the attack",
        "Exploitability: Difficulty of exploiting the vulnerability",
        "Affected Users: Number of users potentially affected",
        "Discoverability: Ease of discovering the vulnerability"
      ]
    }
  ];

  const analysisResultsData = [
    {
      category: "Threat Identification",
      items: [
        "Threat Catalog: Comprehensive list of identified threats",
        "Attack Scenarios: Detailed attack scenario descriptions",
        "Threat Actors: Identification of potential threat actors",
        "Attack Vectors: Mapping of possible attack vectors"
      ]
    },
    {
      category: "Risk Assessment Results",
      items: [
        "Risk Matrix: Visual representation of risk levels",
        "Threat Prioritization: Ranking of threats by risk level",
        "Impact Assessment: Detailed impact analysis for each threat",
        "Likelihood Scores: Probability assessments for threat realization"
      ]
    },
    {
      category: "Recommendations (155 Generated)",
      items: [
        "Mitigation Strategies: Specific mitigation recommendations",
        "Security Controls: Recommended security control implementations",
        "Architecture Changes: Suggested architectural improvements",
        "Process Improvements: Recommended process and procedure changes"
      ]
    }
  ];

  const reportingFeatures = [
    {
      title: "Threat Model Reports",
      features: [
        "Executive Summary: High-level threat assessment overview",
        "Technical Details: Detailed technical threat analysis",
        "Risk Assessment: Comprehensive risk evaluation",
        "Mitigation Roadmap: Prioritized mitigation implementation plan"
      ]
    },
    {
      title: "Visual Documentation",
      features: [
        "Threat Model Diagrams: Visual representation of threat models",
        "Attack Trees: Hierarchical attack scenario visualization",
        "Data Flow Diagrams: System data flow and threat mapping",
        "Risk Heat Maps: Visual risk assessment representations"
      ]
    },
    {
      title: "Compliance Reporting",
      features: [
        "Regulatory Alignment: Mapping to regulatory requirements",
        "Standards Compliance: Alignment with security standards",
        "Audit Documentation: Audit-ready threat modeling documentation",
        "Certification Support: Support for security certification processes"
      ]
    }
  ];

  const bestPractices = [
    {
      category: "Threat Modeling Process",
      practices: [
        "Define Scope: Clearly define the scope of threat modeling",
        "Identify Assets: Catalog all security-relevant assets",
        "Create Architecture Diagrams: Document system architecture",
        "Identify Threats: Systematically identify potential threats",
        "Assess Risks: Evaluate and prioritize identified risks",
        "Define Mitigations: Develop mitigation strategies",
        "Validate and Update: Regularly validate and update threat models"
      ]
    },
    {
      category: "Organizational Implementation",
      practices: [
        "Training: Ensure team members are trained in threat modeling",
        "Process Integration: Integrate threat modeling into development processes",
        "Regular Reviews: Conduct regular threat model reviews and updates",
        "Stakeholder Engagement: Involve relevant stakeholders in threat modeling",
        "Documentation: Maintain comprehensive threat modeling documentation"
      ]
    },
    {
      category: "Continuous Improvement",
      practices: [
        "Lessons Learned: Capture and apply lessons learned from incidents",
        "Methodology Updates: Keep threat modeling methodologies current",
        "Tool Enhancement: Continuously improve threat modeling tools and processes",
        "Industry Alignment: Stay aligned with industry best practices",
        "Metrics and Measurement: Establish metrics for threat modeling effectiveness"
      ]
    }
  ];

  const advancedFeatures = [
    {
      title: "Machine Learning Integration",
      features: [
        "Pattern Recognition: ML-based threat pattern identification",
        "Predictive Analysis: Predictive threat modeling capabilities",
        "Anomaly Detection: Automated detection of unusual threat patterns",
        "Intelligence Augmentation: AI-assisted threat analysis"
      ]
    },
    {
      title: "Collaborative Features",
      features: [
        "Team Collaboration: Multi-user threat modeling capabilities",
        "Review Workflows: Structured review and approval processes",
        "Knowledge Sharing: Shared threat intelligence and knowledge bases",
        "Expert Consultation: Integration with security expert networks"
      ]
    }
  ];

  const getInfoBoxClasses = (type: string) => {
    const baseClasses = "rounded-lg p-4";
    switch (type) {
      case 'blue':
        return `${baseClasses} bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800`;
      case 'green':
        return `${baseClasses} bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800`;
      case 'purple':
        return `${baseClasses} bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800`;
      case 'orange':
        return `${baseClasses} bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800`;
      case 'indigo':
        return `${baseClasses} bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800`;
      case 'red':
        return `${baseClasses} bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800`;
      case 'yellow':
        return `${baseClasses} bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800`;
      default:
        return `${baseClasses} bg-gray-50 dark:bg-black/20 border border-gray-200 dark:border-gray-800`;
    }
  };

  const getInfoTextClasses = (type: string) => {
    switch (type) {
      case 'blue':
        return 'text-blue-900 dark:text-blue-100';
      case 'green':
        return 'text-green-900 dark:text-green-100';
      case 'purple':
        return 'text-purple-900 dark:text-purple-100';
      case 'orange':
        return 'text-orange-900 dark:text-orange-100';
      case 'indigo':
        return 'text-indigo-900 dark:text-indigo-100';
      case 'red':
        return 'text-red-900 dark:text-red-100';
      case 'yellow':
        return 'text-yellow-900 dark:text-yellow-100';
      default:
        return 'text-gray-900 dark:text-gray-100';
    }
  };

  const getInfoItemClasses = (type: string) => {
    switch (type) {
      case 'blue':
        return 'text-blue-800 dark:text-blue-200';
      case 'green':
        return 'text-green-800 dark:text-green-200';
      case 'purple':
        return 'text-purple-800 dark:text-purple-200';
      case 'orange':
        return 'text-orange-800 dark:text-orange-200';
      case 'indigo':
        return 'text-indigo-800 dark:text-indigo-200';
      case 'red':
        return 'text-red-800 dark:text-red-200';
      case 'yellow':
        return 'text-yellow-800 dark:text-yellow-200';
      default:
        return 'text-gray-800 dark:text-gray-200';
    }
  };

  const nextStep = () => {
    setCurrentStepIndex((prev) => (prev + 1) % stepByStepGuide.length);
  };

  const prevStep = () => {
    setCurrentStepIndex((prev) => (prev - 1 + stepByStepGuide.length) % stepByStepGuide.length);
  };

  const nextResult = () => {
    setCurrentResultIndex((prev) => (prev + 1) % analysisResults.length);
  };

  const prevResult = () => {
    setCurrentResultIndex((prev) => (prev - 1 + analysisResults.length) % analysisResults.length);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <ScrollHeader title="Threat Modeling" />
        
        {/* Hero Section */}
        <MotionDiv delay={0.1} variant="fadeIn">
          <div className="text-center mb-12">
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-3xl mx-auto">
              The Threat Modeling module in Ana Security provides systematic threat analysis and risk assessment capabilities. It helps identify, analyze, and prioritize potential security threats to applications, systems, and infrastructure through structured methodologies and automated analysis.
            </p>
            
            <MotionDiv delay={0.2} variant="scale">
              <div className="mb-8">
                <img 
                  src="/screenshots/security-modules/04_threat-modeling.png" 
                  alt="Threat Modeling Dashboard"
                  className="rounded-lg border border-light-border dark:border-dark-border shadow-lg w-full max-w-4xl mx-auto hover:shadow-xl transition-shadow duration-300"
                />
              </div>
            </MotionDiv>
          </div>
        </MotionDiv>

        {/* Current Statistics */}
        <MotionDiv delay={0.3} variant="slideLeft">
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Threat Modeling Dashboard - Current Statistics
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {currentStats.map((stat, index) => (
                <MotionDiv key={index} delay={0.4 + index * 0.1} variant="bounce" hover>
                  <div className="text-center p-6 bg-light-hover dark:bg-dark-hover rounded-lg">
                    <div className="text-3xl text-primary-500 mb-3 flex justify-center">
                      {stat.icon}
                    </div>
                    <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                      {stat.value}
                    </div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {stat.label}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {stat.description}
                    </div>
                  </div>
                </MotionDiv>
              ))}
            </div>

            <div className="bg-primary-500/5 dark:bg-primary-500/10 border border-primary-500/20 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Dashboard Features
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>New Scan Button:</strong> Orange "New Scan" button for initiating new threat modeling analyses</span>
                </div>
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Project Filtering:</strong> "All" dropdown for filtering by specific projects</span>
                </div>
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Repository Links:</strong> Direct access to analyzed repositories</span>
                </div>
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Scan Date Tracking:</strong> Timestamps for all scanning activities</span>
                </div>
              </div>
            </div>
          </div>
        </MotionDiv>

        {/* Step-by-Step Threat Modeling Guide Carousel */}
        <MotionDiv delay={0.8}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Step-by-Step Threat Modeling Guide
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              Follow this comprehensive guide to perform a complete threat modeling scan using ANA Security. This example demonstrates scanning the WebGoat application.
            </p>

            {/* Carousel Container */}
            <div className="relative">
              {/* Navigation Buttons */}
              <div className="flex justify-between items-center mb-6">
                <button
                  onClick={prevStep}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                >
                  <FiChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                
                <div className="flex items-center gap-2">
                  {stepByStepGuide.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentStepIndex(index)}
                      className={`w-3 h-3 rounded-full transition-colors ${
                        index === currentStepIndex 
                          ? 'bg-primary-500' 
                          : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    />
                  ))}
                </div>

                <button
                  onClick={nextStep}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                >
                  Next
                  <FiChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Current Step Content */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-3">
                  <span className="bg-primary-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
                    {stepByStepGuide[currentStepIndex].step}
                  </span>
                  {stepByStepGuide[currentStepIndex].title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {stepByStepGuide[currentStepIndex].description}
                </p>
                <div className="mb-4">
                  <img 
                    src={stepByStepGuide[currentStepIndex].image}
                    alt={stepByStepGuide[currentStepIndex].alt}
                    className="rounded-lg border border-light-border dark:border-dark-border shadow-lg w-full hover:shadow-xl transition-shadow duration-300"
                  />
                </div>
                <div className={getInfoBoxClasses(stepByStepGuide[currentStepIndex].infoType)}>
                  <h4 className={`font-semibold ${getInfoTextClasses(stepByStepGuide[currentStepIndex].infoType)} mb-2`}>
                    {stepByStepGuide[currentStepIndex].infoTitle}
                  </h4>
                  <ul className={`${getInfoItemClasses(stepByStepGuide[currentStepIndex].infoType)} space-y-1 text-sm`}>
                    {stepByStepGuide[currentStepIndex].infoItems.map((item, index) => (
                      <li key={index}>• <strong>{item.split(':')[0]}:</strong> {item.split(':').slice(1).join(':')}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </MotionDiv>

        {/* Threat Analysis Results Carousel */}
        <MotionDiv delay={0.9}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Threat Analysis Results - WebGoat Example
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              After the analysis is complete, you'll receive comprehensive results across multiple categories. Here's an example from a WebGoat application scan.
            </p>

            {/* Carousel Container */}
            <div className="relative">
              {/* Navigation Buttons */}
              <div className="flex justify-between items-center mb-6">
                <button
                  onClick={prevResult}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                >
                  <FiChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                
                <div className="flex items-center gap-2">
                  {analysisResults.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentResultIndex(index)}
                      className={`w-3 h-3 rounded-full transition-colors ${
                        index === currentResultIndex 
                          ? 'bg-primary-500' 
                          : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    />
                  ))}
                </div>

                <button
                  onClick={nextResult}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                >
                  Next
                  <FiChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Current Result Content */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  {analysisResults[currentResultIndex].title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {analysisResults[currentResultIndex].description}
                </p>
                <div className="mb-4">
                  <img 
                    src={analysisResults[currentResultIndex].image}
                    alt={analysisResults[currentResultIndex].alt}
                    className="rounded-lg border border-light-border dark:border-dark-border shadow-lg w-full hover:shadow-xl transition-shadow duration-300"
                  />
                </div>
                <div className={getInfoBoxClasses(analysisResults[currentResultIndex].infoType)}>
                  <h4 className={`font-semibold ${getInfoTextClasses(analysisResults[currentResultIndex].infoType)} mb-2`}>
                    {analysisResults[currentResultIndex].infoTitle}
                  </h4>
                  <ul className={`${getInfoItemClasses(analysisResults[currentResultIndex].infoType)} space-y-1 text-sm`}>
                    {analysisResults[currentResultIndex].infoItems.map((item, index) => (
                      <li key={index}>• {item}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Sample Report Section */}
              <div className="bg-gray-50 dark:bg-black/20 border border-gray-200 dark:border-gray-800 rounded-lg p-6">
                <div className="flex items-center gap-4 mb-4">
                  <FiCheckCircle className="w-6 h-6 text-green-500" />
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">WebGoat Threat Model Report</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Generated on 2025-05-25T12:19:57</p>
                  </div>
                </div>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  A comprehensive PDF report is generated containing all analysis results, findings, and recommendations. 
                  The report includes executive summary, technical details, risk assessments, and implementation roadmaps.
                </p>
                <div className="flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400">
                  <FiCheckCircle className="w-4 h-4" />
                  <span>Download available in PDF format</span>
                </div>
              </div>
            </div>
          </div>
        </MotionDiv>

        {/* Core Features */}
        <MotionDiv delay={0.2}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Threat Modeling Core Features
            </h2>
            
            <div className="space-y-8">
              {coreFeatures.map((feature, index) => (
                <div key={index} className="border border-light-border dark:border-dark-border rounded-lg p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="text-2xl text-primary-500">
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {feature.title}
                    </h3>
                  </div>
                  <ul className="space-y-3">
                    {feature.features.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-700 dark:text-gray-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Threat Modeling Methodologies */}
        <MotionDiv delay={0.3}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Threat Modeling Methodologies
            </h2>
            
            <div className="space-y-8">
              {threatMethodologies.map((methodology, index) => (
                <div key={index} className="border border-light-border dark:border-dark-border rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    {methodology.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {methodology.description}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {methodology.components.map((component, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-light-hover dark:bg-dark-hover rounded-lg">
                        <FiShield className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{component}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Analysis Results */}
        <MotionDiv delay={0.4}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Threat Analysis Results
            </h2>
            
            <div className="space-y-8">
              {analysisResultsData.map((category, index) => (
                <div key={index}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {category.category}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {category.items.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-light-hover dark:bg-dark-hover rounded-lg">
                        <FiEye className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Reporting and Documentation */}
        <MotionDiv delay={0.5}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Reporting and Documentation
            </h2>
            
            <div className="space-y-8">
              {reportingFeatures.map((feature, index) => (
                <div key={index}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {feature.title}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {feature.features.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-light-hover dark:bg-dark-hover rounded-lg">
                        <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Advanced Features */}
        <MotionDiv delay={0.6}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Advanced Features
            </h2>
            
            <div className="space-y-8">
              {advancedFeatures.map((feature, index) => (
                <div key={index}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {feature.title}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {feature.features.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-light-hover dark:bg-dark-hover rounded-lg">
                        <FiSettings className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Best Practices */}
        <MotionDiv delay={0.7}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Threat Modeling Best Practices
            </h2>
            
            <div className="space-y-8">
              {bestPractices.map((category, index) => (
                <div key={index}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {category.category}
                  </h3>
                  <div className="space-y-3">
                    {category.practices.map((practice, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{practice}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Call to Action */}
        <MotionDiv delay={1.0}>
          <div className="text-center bg-primary-500/5 dark:bg-primary-500/10 border border-primary-500/20 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Ready to Model Your Threats?
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              Start using Threat Modeling to systematically identify and mitigate security threats in your applications.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <a 
                href="/ana-actions" 
                className="block p-6 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg hover:border-primary-500/30 transition-colors group"
              >
                <FiCheckCircle className="w-8 h-8 text-primary-500 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Use ANA Actions</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Implement threat mitigation strategies</p>
              </a>
              
              <a 
                href="/dashboard" 
                className="block p-6 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg hover:border-primary-500/30 transition-colors group"
              >
                <FiTrendingUp className="w-8 h-8 text-primary-500 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">View Dashboard</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Monitor threat analysis results</p>
              </a>
              
              <a 
                href="/integrations" 
                className="block p-6 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg hover:border-primary-500/30 transition-colors group"
              >
                <FiSettings className="w-8 h-8 text-primary-500 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Setup Integrations</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Connect your development workflow</p>
              </a>
            </div>
          </div>
        </MotionDiv>
      </motion.div>
    </div>
  );
};

export default ThreatModeling;

